import React, { useEffect, useState, useRef } from 'react';
import { Plus, Edit, Trash2, Upload, X, FileText, Video, Music, Image, File, Sparkles } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import FileUploader from '../../components/FileUploader';
import { getImageUrl } from '../../utils/imageUrl';
import AIImportWidget from '../../components/AIImportWidget';

const FILE_TYPE_ICONS = {
  image: Image,
  mp4: Video,
  mp3: Music,
  pdf: FileText,
  doc: File,
  file: File
};

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [showNewCategory, setShowNewCategory] = useState(false);
  const [showNewSubCategory, setShowNewSubCategory] = useState(false);
  const [newCategory, setNewCategory] = useState('');
  const [newSubCategory, setNewSubCategory] = useState('');
  const [selectedParentCat, setSelectedParentCat] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    original_price: '',
    category: '',
    subcategory: '',
    images: [],
    video_url: '',
    files: [],
    external_links: [],
    featured: false,
    in_stock: true,
    stock_quantity: ''
  });
  const [imageUrl, setImageUrl] = useState('');
  const [linkName, setLinkName] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [showAIImport, setShowAIImport] = useState(false);
  const imageInputRef = useRef(null);

  // Fetch categories from backend
  const fetchCategories = async () => {
    try {
      const response = await api.get('/products/categories');
      if (response.data && response.data.length > 0) {
        // Separate main categories and sub-categories
        const mainCats = response.data.filter(c => !c.parent_id);
        const subCats = response.data.filter(c => c.parent_id);
        setCategories(mainCats);
        setSubCategories(subCats);
      }
    } catch (error) {
      console.log('Using default categories');
      setCategories([
        {name: 'Software', id: 'software'},
        {name: 'Digital Bundles', id: 'digital-bundles'},
        {name: 'Templates', id: 'templates'},
        {name: 'Other', id: 'other'}
      ]);
    }
  };

  // Add new category
  const handleAddCategory = async () => {
    if (!newCategory.trim()) return;
    const trimmed = newCategory.trim();
    
    try {
      const response = await api.post('/products/categories', { name: trimmed });
      fetchCategories(); // Refresh list
      setFormData({ ...formData, category: trimmed });
      setNewCategory('');
      setShowNewCategory(false);
      toast.success('Category added!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add category');
    }
  };

  // Add new sub-category
  const handleAddSubCategory = async () => {
    if (!newSubCategory.trim() || !selectedParentCat) return;
    
    try {
      await api.post('/products/categories', { 
        name: newSubCategory.trim(),
        parent_id: selectedParentCat
      });
      fetchCategories();
      setFormData({ ...formData, subcategory: newSubCategory.trim() });
      setNewSubCategory('');
      setSelectedParentCat('');
      setShowNewSubCategory(false);
      toast.success('Sub-category added!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add sub-category');
    }
  };

  // Get sub-categories for selected category
  const getSubCategoriesForCategory = (categoryName) => {
    const parentCat = categories.find(c => c.name === categoryName);
    if (!parentCat) return [];
    return subCategories.filter(sc => sc.parent_id === parentCat.id);
  };

  // Handle AI Import data
  const handleAIImportData = (data) => {
    setFormData(prev => ({
      ...prev,
      name: data.name || prev.name,
      description: data.description || prev.description,
      price: data.price ? String(data.price) : prev.price,
      original_price: data.mrp ? String(data.mrp) : prev.original_price,
      category: data.category || prev.category,
      subcategory: data.subcategory || prev.subcategory,
      images: data.images && data.images.length > 0 ? data.images : prev.images,
      stock_quantity: data.stock ? String(data.stock) : prev.stock_quantity,
    }));
    setShowAIImport(false);
    toast.success('Form auto-filled with extracted data!');
  };

  const fetchProducts = async () => {
    try {
      const response = await api.get('/products');
      setProducts(response.data);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    }
  };

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const data = {
        ...formData,
        price: parseFloat(formData.price),
        original_price: formData.original_price ? parseFloat(formData.original_price) : null,
        stock_quantity: formData.stock_quantity ? parseInt(formData.stock_quantity) : null
      };

      if (editingProduct) {
        await api.put(`/products/${editingProduct.id}`, data);
        toast.success('Product updated successfully');
      } else {
        await api.post('/products', data);
        toast.success('Product created successfully');
      }

      setShowModal(false);
      setEditingProduct(null);
      setFormData({
        name: '',
        description: '',
        price: '',
        original_price: '',
        category: 'Software',
        subcategory: '',
        images: [],
        video_url: '',
        files: [],
        external_links: [],
        featured: false,
        in_stock: true,
        stock_quantity: ''
      });
      setImageUrl('');
      setLinkName('');
      setLinkUrl('');
      fetchProducts();
    } catch (error) {
      // Handle validation errors which come as objects
      let errorMessage = 'Failed to save product';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          // Pydantic validation errors come as array of objects
          errorMessage = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        } else if (typeof detail === 'object') {
          errorMessage = detail.msg || detail.message || JSON.stringify(detail);
        }
      }
      toast.error(errorMessage);
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      description: product.description,
      price: product.price.toString(),
      original_price: product.original_price?.toString() || '',
      category: product.category,
      subcategory: product.subcategory || '',
      images: product.images || [],
      video_url: product.video_url || '',
      files: product.files || [],
      external_links: product.external_links || [],
      featured: product.featured,
      in_stock: product.in_stock !== false,
      stock_quantity: product.stock_quantity?.toString() || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    
    try {
      await api.delete(`/products/${id}`);
      toast.success('Product deleted');
      fetchProducts();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  // Handle product image upload from device
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload an image file (JPEG, PNG, GIF, WebP)');
      return;
    }
    
    setUploadingImage(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const uploadRes = await api.post('/upload/content/product', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (uploadRes.data.success) {
        setFormData({
          ...formData,
          images: [...formData.images, uploadRes.data.url]
        });
        toast.success('Image uploaded!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploadingImage(false);
      if (imageInputRef.current) {
        imageInputRef.current.value = '';
      }
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-16 px-6" data-testid="admin-products-page">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-4xl md:text-5xl font-bold" data-testid="products-title">Manage Products</h1>
          <button
            onClick={() => setShowModal(true)}
            className="btn-primary flex items-center gap-2"
            data-testid="add-product-button"
          >
            <Plus className="w-5 h-5" />
            Add Product
          </button>
        </div>

        {/* Products Table */}
        <div className="glass-card p-6">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-4 px-4">Name</th>
                  <th className="text-left py-4 px-4">Category</th>
                  <th className="text-left py-4 px-4">Price</th>
                  <th className="text-left py-4 px-4">Featured</th>
                  <th className="text-right py-4 px-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {products.map((product) => (
                  <tr key={product.id} className="border-b border-white/5" data-testid={`product-row-${product.id}`}>
                    <td className="py-4 px-4 font-semibold">{product.name}</td>
                    <td className="py-4 px-4 text-muted-foreground">{product.category}</td>
                    <td className="py-4 px-4 text-primary font-bold">₹{product.price}</td>
                    <td className="py-4 px-4">
                      {product.featured && (
                        <span className="px-2 py-1 bg-accent/20 text-accent rounded-full text-xs">Featured</span>
                      )}
                    </td>
                    <td className="py-4 px-4 text-right">
                      <button
                        onClick={() => handleEdit(product)}
                        className="text-primary hover:text-primary/80 mr-3"
                        data-testid={`edit-product-${product.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(product.id)}
                        className="text-destructive hover:text-destructive/80"
                        data-testid={`delete-product-${product.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-6" data-testid="product-modal">
            <div className="glass-card p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">
                  {editingProduct ? 'Edit Product' : 'Add New Product'}
                </h2>
                {!editingProduct && (
                  <button
                    type="button"
                    onClick={() => setShowAIImport(!showAIImport)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                      showAIImport 
                        ? 'bg-purple-500 text-white' 
                        : 'bg-purple-500/20 text-purple-400 hover:bg-purple-500/30'
                    }`}
                  >
                    <Sparkles className="w-4 h-4" />
                    AI Import
                  </button>
                )}
              </div>
              
              {/* AI Import Widget */}
              {showAIImport && !editingProduct && (
                <AIImportWidget 
                  onDataExtracted={handleAIImportData}
                  listingType="product"
                />
              )}
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Product Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary focus:ring-1 focus:ring-primary/50"
                    data-testid="product-name-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    required
                    rows={4}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary focus:ring-1 focus:ring-primary/50"
                    data-testid="product-description-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Offer Price (₹)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                      required
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary focus:ring-1 focus:ring-primary/50"
                      data-testid="product-price-input"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Discounted selling price</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">Regular Price (₹)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.original_price}
                      onChange={(e) => setFormData({ ...formData, original_price: e.target.value })}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary focus:ring-1 focus:ring-primary/50"
                      data-testid="product-original-price-input"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Original price (before discount)</p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Category</label>
                  {!showNewCategory ? (
                    <div className="flex gap-2">
                      <select
                        value={formData.category}
                        onChange={(e) => {
                          if (e.target.value === '__new__') {
                            setShowNewCategory(true);
                          } else {
                            setFormData({ ...formData, category: e.target.value, subcategory: '' });
                          }
                        }}
                        className="flex-1 px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary focus:ring-1 focus:ring-primary/50"
                        data-testid="product-category-select"
                      >
                        <option value="">-- Select Category --</option>
                        {categories.map(cat => (
                          <option key={cat.id || cat.name} value={cat.name}>{cat.icon && `${cat.icon} `}{cat.name}</option>
                        ))}
                        <option value="__new__">+ Add New Category</option>
                      </select>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newCategory}
                        onChange={(e) => setNewCategory(e.target.value)}
                        placeholder="Enter new category name"
                        className="flex-1 px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary"
                        autoFocus
                        data-testid="new-category-input"
                      />
                      <button
                        type="button"
                        onClick={handleAddCategory}
                        className="px-4 py-2 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30"
                      >
                        Add
                      </button>
                      <button
                        type="button"
                        onClick={() => { setShowNewCategory(false); setNewCategory(''); }}
                        className="px-4 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30"
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Sub-Category</label>
                  {!showNewSubCategory ? (
                    <div className="flex gap-2">
                      <select
                        value={formData.subcategory}
                        onChange={(e) => {
                          if (e.target.value === '__new__') {
                            setShowNewSubCategory(true);
                          } else {
                            setFormData({ ...formData, subcategory: e.target.value });
                          }
                        }}
                        className="flex-1 px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary focus:ring-1 focus:ring-primary/50"
                        data-testid="product-subcategory-select"
                      >
                        <option value="">-- Select Sub-Category --</option>
                        {getSubCategoriesForCategory(formData.category).map(sub => (
                          <option key={sub.id} value={sub.name}>{sub.icon && `${sub.icon} `}{sub.name}</option>
                        ))}
                        <option value="__new__">+ Add New Sub-Category</option>
                      </select>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <select
                        value={selectedParentCat}
                        onChange={(e) => setSelectedParentCat(e.target.value)}
                        className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl"
                      >
                        <option value="">-- Select Parent Category --</option>
                        {categories.map(cat => (
                          <option key={cat.id || cat.name} value={cat.id}>{cat.name}</option>
                        ))}
                      </select>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={newSubCategory}
                          onChange={(e) => setNewSubCategory(e.target.value)}
                          placeholder="Enter sub-category name"
                          className="flex-1 px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary"
                          data-testid="new-subcategory-input"
                        />
                        <button
                          type="button"
                          onClick={handleAddSubCategory}
                          className="px-4 py-2 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30"
                        >
                          Add
                        </button>
                        <button
                          type="button"
                          onClick={() => { setShowNewSubCategory(false); setNewSubCategory(''); setSelectedParentCat(''); }}
                          className="px-4 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                      <Video className="w-4 h-4" />
                      Product Video URL
                    </label>
                    <input
                      type="url"
                      value={formData.video_url}
                      onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
                      placeholder="https://youtube.com/watch?v=..."
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary focus:ring-1 focus:ring-primary/50"
                      data-testid="product-video-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Stock Quantity</label>
                    <input
                      type="number"
                      value={formData.stock_quantity}
                      onChange={(e) => setFormData({ ...formData, stock_quantity: e.target.value })}
                      placeholder="Leave empty for unlimited"
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary focus:ring-1 focus:ring-primary/50"
                      data-testid="product-stock-input"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.featured}
                      onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                      className="w-4 h-4 rounded"
                    />
                    <span className="text-sm">Featured Product</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.in_stock}
                      onChange={(e) => setFormData({ ...formData, in_stock: e.target.checked })}
                      className="w-4 h-4 rounded"
                    />
                    <span className="text-sm">In Stock</span>
                  </label>
                </div>

                {/* Product Images */}
                <div className="border border-secondary/30 rounded-xl p-4 bg-secondary/5">
                  <label className="block text-sm font-medium mb-3 flex items-center gap-2">
                    <Image className="w-4 h-4 text-secondary" />
                    Product Images
                  </label>
                  
                  {/* Upload from Device */}
                  <div className="mb-4">
                    <input
                      type="file"
                      ref={imageInputRef}
                      onChange={handleImageUpload}
                      accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                      className="hidden"
                      data-testid="product-image-upload-input"
                    />
                    <button
                      type="button"
                      onClick={() => imageInputRef.current?.click()}
                      disabled={uploadingImage}
                      className="w-full border-2 border-dashed border-white/20 rounded-xl p-4 text-center hover:border-secondary/50 transition-colors"
                    >
                      {uploadingImage ? (
                        <div className="flex items-center justify-center gap-2">
                          <div className="w-5 h-5 border-2 border-secondary border-t-transparent rounded-full animate-spin" />
                          <span>Uploading...</span>
                        </div>
                      ) : (
                        <div>
                          <Upload className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                          <p className="text-sm font-medium">Click to upload image from device</p>
                          <p className="text-xs text-muted-foreground">JPEG, PNG, GIF, WebP</p>
                        </div>
                      )}
                    </button>
                  </div>
                  
                  {/* Or add via URL */}
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground">Or add image via URL:</p>
                    <div className="flex gap-2">
                      <input
                        type="url"
                        value={imageUrl}
                        onChange={(e) => setImageUrl(e.target.value)}
                        placeholder="https://example.com/image.jpg"
                        className="flex-1 px-4 py-2 bg-white/5 border border-white/10 rounded-lg focus:border-primary"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          if (imageUrl.trim()) {
                            setFormData({...formData, images: [...formData.images, imageUrl.trim()]});
                            setImageUrl('');
                          }
                        }}
                        className="px-4 py-2 bg-secondary/20 text-secondary rounded-lg hover:bg-secondary/30"
                      >
                        Add
                      </button>
                    </div>
                    {formData.images.length > 0 && (
                      <div className="space-y-2 mt-3">
                        <p className="text-xs text-muted-foreground">Added Images ({formData.images.length}):</p>
                        {formData.images.map((img, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-sm bg-white/5 p-2 rounded-lg">
                            <img src={getImageUrl(img)} alt="" className="w-10 h-10 rounded object-cover" onError={(e) => e.target.style.display='none'} />
                            <span className="flex-1 truncate text-xs">{img}</span>
                            <button
                              type="button"
                              onClick={() => setFormData({...formData, images: formData.images.filter((_, i) => i !== idx)})}
                              className="text-red-500 hover:text-red-400 p-1"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Product Files - Device Upload */}
                <div className="border border-primary/30 rounded-xl p-4 bg-primary/5">
                  <label className="block text-sm font-medium mb-3 flex items-center gap-2">
                    <Upload className="w-4 h-4 text-primary" />
                    Product Files (Upload from Device)
                  </label>
                  <FileUploader
                    contentType="product"
                    existingFiles={formData.files || []}
                    onFileUploaded={(file) => {
                      setFormData({
                        ...formData,
                        files: [...(formData.files || []), file]
                      });
                    }}
                    onFileRemoved={(idx) => {
                      setFormData({
                        ...formData,
                        files: (formData.files || []).filter((_, i) => i !== idx)
                      });
                    }}
                  />
                  <p className="text-xs text-muted-foreground mt-2">
                    Upload product files: JPEG, GIF, PNG images, MP4 videos, MP3 audio, PDF documents
                  </p>
                </div>

                {/* External Links */}
                <div>
                  <label className="block text-sm font-medium mb-2">External Links</label>
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={linkName}
                        onChange={(e) => setLinkName(e.target.value)}
                        placeholder="Link name (e.g., Google Drive)"
                        className="flex-1 px-4 py-2 bg-white/5 border border-white/10 rounded-lg focus:border-primary"
                      />
                      <input
                        type="url"
                        value={linkUrl}
                        onChange={(e) => setLinkUrl(e.target.value)}
                        placeholder="Link URL"
                        className="flex-1 px-4 py-2 bg-white/5 border border-white/10 rounded-lg focus:border-primary"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          if (linkName.trim() && linkUrl.trim()) {
                            setFormData({...formData, external_links: [...formData.external_links, {name: linkName.trim(), url: linkUrl.trim()}]});
                            setLinkName('');
                            setLinkUrl('');
                          }
                        }}
                        className="px-4 py-2 bg-accent/20 text-accent rounded-lg hover:bg-accent/30"
                      >
                        Add Link
                      </button>
                    </div>
                    {formData.external_links.length > 0 && (
                      <div className="space-y-1">
                        {formData.external_links.map((link, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-sm bg-white/5 p-2 rounded">
                            <span className="flex-1">{link.name}</span>
                            <button
                              type="button"
                              onClick={() => setFormData({...formData, external_links: formData.external_links.filter((_, i) => i !== idx)})}
                              className="text-destructive"
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.featured}
                      onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                      className="w-5 h-5"
                      data-testid="product-featured-checkbox"
                    />
                    <span className="text-sm font-medium">Featured Product</span>
                  </label>
                </div>

                <div className="flex gap-4 pt-4">
                  <button
                    type="submit"
                    className="flex-1 btn-primary"
                    data-testid="save-product-button"
                  >
                    {editingProduct ? 'Update' : 'Create'} Product
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditingProduct(null);
                      setFormData({
                        name: '',
                        description: '',
                        price: '',
                        original_price: '',
                        category: 'Software',
                        images: [],
                        files: [],
                        external_links: [],
                        featured: false
                      });
                      setImageUrl('');
                      setLinkName('');
                      setLinkUrl('');
                    }}
                    className="px-8 py-4 bg-white/5 text-foreground border border-white/10 hover:bg-white/10 rounded-full font-semibold"
                    data-testid="cancel-button"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminProducts;
